import classNames from 'classnames';
import React, { CSSProperties, FC, ReactNode, useEffect } from 'react';
import styled, { Interpolation } from 'styled-components';

import { changeScrollBehavior } from 'src/common/helpers';

interface ModalProps {
    size: 'sm' | 'md' | 'lg' | 'xl';
    open: boolean;
    children?: ReactNode;
    className?: string;
    styles?: Interpolation<CSSProperties>;
    isBackdropHidden?: boolean;
}
interface ModalStyledProps {
    size: 'sm' | 'md' | 'lg' | 'xl';
    styles?: Interpolation<CSSProperties>;
}

const ModalContainer = styled.div<ModalStyledProps>`
    ${({ theme: { colors, breakpoints }, size }) => `
        position: fixed;
        z-index: 11001;
        inset: 0px;
        margin: auto;
        width: max-content;
        .modal-container {
            height: 100%;
            outline: 0;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        .modal-paper {
            background: ${colors.background.secondary.v2};
            box-shadow: 0px 11px 15px rgb(0 0 0 / 20%), 0px 9px 46px rgb(0 0 0 / 12%), 0px 24px 38px rgb(0 0 0 / 14%);
            border-radius: 4px;
            transition: box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
            display: flex;
            max-height: calc(100% - 64px);
            overflow-y: auto;
            flex-direction: column;
            max-width: none;
            ${
                size === 'sm'
                    ? `
                        width: 25%;
                        @media (max-width: ${breakpoints.lg}) {
                            width: 35%;
                        }          
                    `
                    : size === 'md'
                    ? `
                        width: 50%;
                        @media (max-width: ${breakpoints.lg}) {
                            width: 60%;
                        }
                    `
                    : size === 'lg'
                    ? `
                        width: 65%;
                        @media (max-width: ${breakpoints.lg}) {
                            width: 70%;
                        }
                    `
                    : `
                        width: 80%;
                        @media (max-width: ${breakpoints.lg}) {
                            width: 85%;
                        }
                    `
            }
            @media (max-width: ${breakpoints.md}) {
                width: 90%;
            }
        }
   `}
    ${({ styles }) => styles};
`;

const ModalBackdrop = styled.div`
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    z-index: -1;
    position: fixed;
    align-items: center;
    justify-content: center;
    background-color: ${({ theme: { colors } }) => colors.background.overlay.v5};
    -webkit-tap-highlight-color: transparent;
`;

const Modal: FC<ModalProps> = ({
    children,
    styles,
    className,
    open,
    size,
    isBackdropHidden = false,
}) => {
    // TODO: fixed scroll behavior call, it should be removed from all the modals
    useEffect(() => {
        open && changeScrollBehavior(true);
        return () => {
            changeScrollBehavior(false);
        };
    }, [open]);
    return open ? (
        <ModalContainer className={classNames('modal', className)} styles={styles} size={size}>
            {!isBackdropHidden && (
                <ModalBackdrop
                    aria-hidden="true"
                    style={{
                        opacity: 1,
                        transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                    }}
                />
            )}
            <div
                className={'modal-container'}
                style={{ opacity: 1, transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms' }}
            >
                <div className={'modal-paper'}>{children}</div>
            </div>
        </ModalContainer>
    ) : (
        <></>
    );
};

export default Modal;
